import { useContext, useMemo, useState, useEffect } from 'react'
import { css } from '@emotion/react'
import Pitch from '../../context/Pitch'
import { colors, fonts, timings, ScoreIndicator, backendColors, getBestTrainingGoalFromIndexResults, getSortedPerformanceIndexResults } from 'shared-ui'
import HealthEffectsMap from '../physicalAssessment/HealthEffectsMap'
import EffectsList from '../physicalAssessment/EffectsList'
import { BodyPartProvider } from '../../context/BodyPart'
import { useIntl } from 'react-intl'
import { SurfaceLayerProvider } from '../../context/SurfaceLayer'
import IndexGauge from './IndexGauge'
import FancyDropdown from '../_control/FancyDropdown'
import BootIndexIcon from '../analysis/BootIndexIcon'
import { rgba } from 'polished'
import useRawState from '../../hooks/useRawState'
import useBridge from '../../hooks/useBridge'
import IndexResultsModal from './IndexResultsModal'
import PitchTypeIcon from '../pitch/PitchTypeIcon'
import BootAdviceModal from './BootAdviceModal'
import { setHashParams } from '../../utils/urlHashParams'
import PageSubtitle from '../_general/PageSubtitle'
import TrainingGoalZones from './TrainingGoalZones'
import PitchIndexPdfContext from '../../context/PitchIndexPdfData'

export default function IndexOverviewCard({
    indexData = [],
    noNavigate = false,
    referenceTimestamp,
    showExpired = true,
    setShowExpired = () => { },
    ...props
}) {
    const { formatMessage } = useIntl()

    if (!referenceTimestamp) throw new Error('IndexOverviewCard requires a referenceTimestamp')

    const { data: pitch } = useContext(Pitch)

    const { data: indexCategories, isBusy: loadingIndexCategories } = useRawState(
        '/api/current/index-categories',
        { pagination: false }
    )

    // best suited for (finds highest scoring training-goal that is not expired)
    const bestSuitedIndex = useMemo(() => {
        if (!indexData) return
        const indexCategory = getBestTrainingGoalFromIndexResults(indexData, new Date(referenceTimestamp * 1000))
        return indexCategory?.indexCategory ? indexCategory : undefined
    }, [indexData])

    const { setSelectedTrainingGoalId } = useContext(PitchIndexPdfContext)

    // training goal selector
    const [selectedTrainingGoal, setSelectedTrainingGoal] = useState(null)

    // keep the PDF context in sync with whichever training goal is currently shown
    useEffect(() => {
        setSelectedTrainingGoalId(selectedTrainingGoal)
    }, [selectedTrainingGoal])
    
    const trainingGoalOptions = useMemo(() => {

        if (!pitch || loadingIndexCategories || !indexCategories) return []

        const sportFallback = pitch.club.sport || 'football'
        const filtered = indexCategories
            .filter(cat => cat.indexCategoryGroup === 'training-goal' && cat.sport === sportFallback)
            .toSorted((a, b) => (a.sortRank ?? 0) - (b.sortRank ?? 0))

        return [
            ...filtered.map(cat => {
                return {
                    value: cat.id,
                    label: cat.name,
                }
            })
        ]
    }, [indexCategories, loadingIndexCategories, pitch])

    // auto select best traininGoal first
    useEffect(() => {
        if (!bestSuitedIndex?.indexCategory?.id) return
        setSelectedTrainingGoal(bestSuitedIndex.indexCategory.id)
    }, [bestSuitedIndex])

    //boot advice
    const { data: bootTypes, loading: loadingBootTypes } = useBridge('/api/current/frontend-bridge/boot-types')

    const { bootType, bootName, bootExpired } = useMemo(() => {
        const index = indexData?.find(ind => ind.indexCategory.indexCategoryGroup === 'boot')
        if (!index || loadingBootTypes) return {}
        const recommendedBootType = index.bootRecommendations?.[0]?.bootType
        return {
            bootType: recommendedBootType,
            bootName: bootTypes[recommendedBootType],
            bootExpired: index._isExpired_,
        }
    }, [indexData, bootTypes, loadingBootTypes])

    const [showModal, setShowModal] = useState(false)
    const [showBootModal, setShowBootModal] = useState(false)

    useEffect(() => {
        // only one can be active at a time so we dont have to worry about overwriting
        if (showModal) {
            setHashParams(prev => ({ ...prev, modal: 'trainingGoals' }))
        } else if (showBootModal) {
            setHashParams(prev => ({ ...prev, modal: 'bootAdvice' }))
        } else {
            setHashParams(prev => {
                const copy = { ...prev }
                delete copy.modal
                return copy
            })
        }
    }, [showModal, showBootModal])

    const allExpired = indexData.every(ind => ind._isExpired_)
    const allResultsHidden = indexData.length > 0 && allExpired && !showExpired

    if (allResultsHidden) return <div>
        <PageSubtitle >
            {formatMessage({ id: 'noData' })}
            &nbsp;&nbsp;
            <span css={appearance.allExpired} onClick={() => setShowExpired(true)}>
                {formatMessage({ id: 'toggleVisibilityOfOldData' })}
            </span>
        </PageSubtitle>
    </div>

    return <>
        <div
            css={style.card(colors.super)}
            style={{ textDecoration: 'none' }} /* Necessary for pdf-gen */
            {...props}
        >

            <Header
                pitch={pitch}
                indexData={indexData}
                bestSuitedInd={bestSuitedIndex}
                setShowModal={setShowModal}
                showExpired={showExpired}
            />

            <div css={style.bodyWrapper}>

                <HealthEffectsAndHighlightsSection indexData={indexData} showExpired={showExpired} />

                <div css={css`display: flex; justify-content: center; gap: 0 2em;`}>
                    <TrainingGoalSection
                        trainingGoalOptions={trainingGoalOptions}
                        selectedTrainingGoal={selectedTrainingGoal}
                        setSelectedTrainingGoal={setSelectedTrainingGoal}
                        indexData={indexData}
                        pitch={pitch}
                        showExpired={showExpired}
                    />
                    <BootSection
                        bootType={bootType}
                        bootName={bootName}
                        setShowBootModal={setShowBootModal}
                        showExpired={showExpired}
                        css={[css`
                            min-width: 8em; 
                            @media screen and (max-width: 600px) {
                                min-width: 4em;
                            }
                        `, bootExpired ? style.isExpired(showExpired) : null]}
                    />
                </div>
            </div>
        </div >
        {showModal &&
            <IndexResultsModal
                pitch={pitch}
                indexData={indexData}
                referenceTimestamp={referenceTimestamp * 1000}
                onClickClose={() => setShowModal(false)}
                showExpired={showExpired}
            />
        }
        {showBootModal &&
            <BootAdviceModal
                pitch={pitch}
                indexData={indexData}
                bootTypes={bootTypes}
                onClickClose={() => setShowBootModal(false)}
            />
        }
    </>
}

function Header({ indexData, pitch, bestSuitedInd, setShowModal, showExpired, ...props }) {

    const { formatMessage } = useIntl()

    const bestSuitedTitle = indexData.length === 0 ?
        formatMessage({ id: "noData" })
        : formatMessage({ id: "bestSuitedFor" })

    const bestSuitedName = bestSuitedInd?.indexCategory?.name || (
        indexData.length === 0 ?
            ''
            : formatMessage({ id: "notApplicableAbbr" })
    )

    const bestSuitedExpired = bestSuitedInd?._isExpired_

    const showScore = !!bestSuitedInd

    const valStr = '' + Math.round(bestSuitedInd?.value * 10) / 10

    return <div css={[style.header, appearance.header]}>

        <div css={style.headerLeftSection}>
            <div title={pitch?.type} css={style.headerPitch} >
                <PitchTypeIcon
                    pitch={pitch}
                    css={appearance.pitchTypeIcon}
                />
                <h4 css={style.title}>
                    {pitch?.name}
                </h4>
            </div>

            <div css={style.headerBestSuited}>
                <span css={css`margin-left: 0.5em; color: ${colors.light};`}>
                    {bestSuitedTitle}&nbsp;&nbsp;
                </span>
                <div onClick={() => setShowModal(true)} css={[
                    style.bestSuitedResult,
                    bestSuitedExpired ? style.isExpired(showExpired) : null
                ]}>
                    {bestSuitedName}
                    {showScore &&
                        <ScoreIndicator
                            color={backendColors[bestSuitedInd?.score?.assessment?.frontendCode]}
                            valueString={valStr}
                            notApplicableAbbrString={formatMessage({ id: "notApplicableAbbr" })}
                            css={css`font-size: 0.65em; color: white !important;`}
                        />
                    }
                </div>
            </div>
        </div>

        <div css={css`display: flex; justify-content: center; flex-wrap: wrap; gap: 1em;`}>
            {getSortedPerformanceIndexResults(indexData)
                .map((ind, i) => {
                    return <div
                        key={`indG-${i}`}
                        css={ind._isExpired_ ? style.isExpired(showExpired) : null}
                    >
                        <IndexGauge
                            size={80}
                            indexData={{
                                localisedName: ind.indexCategory?.name,
                                indexValue: ind.value,
                                frontendCode: ind.score?.assessment?.frontendCode,
                                indexType: ind.indexCategory.indexCategoryGroup,
                            }}
                        />
                    </div>
                })}
        </div>
    </div>
}

function HealthEffectsAndHighlightsSection({ indexData, showExpired, ...props }) {

    const { formatMessage } = useIntl()

    const virtualTests = [...indexData]// for the time being, index has the same health effects props as tests do
    //filter out expired index results, fall back to expired if no unexpired, but grey out everything
    const filtered = virtualTests.filter(ind => !ind._isExpired_)
    const useFallback = filtered.length === 0
    const testsToUse = useFallback ? virtualTests : filtered

    return <BodyPartProvider><SurfaceLayerProvider>
        <section
            css={[css`
                display: flex;
                gap: 1em 2em;
                flex-grow: 1;
                @media screen and (max-width: 800px) {
                    flex-direction: column;
                }
            `, useFallback ? style.isExpired(showExpired) : null]}
            {...props}
        >
            <div css={css`display: grid; place-items: center;`}>
                <h3 css={[css`place-self: start; margin-bottom: 0.875em;`, appearance.h3]}>
                    {formatMessage({ id: "athlete" })}
                </h3>
                <div css={css`width: 23em;`}>
                    <HealthEffectsMap tests={testsToUse} />
                </div>
            </div>

            <div css={css`
                width: 100%; 
                display: flex; 
                flex-direction: column;

                @media screen and (max-width: 600px) {
                    align-self: stretch;
                    width: 100%;
                    max-width: 100%;
                    min-width: 0;
                }
            `}>
                <h3 css={[css`margin-bottom: 0.875em;`, appearance.h3]}>
                    {formatMessage({ id: 'performanceAndWelfareInfluencers' })}
                </h3>
                <EffectsList
                    tests={testsToUse}
                    type='player'
                    css={[css`height: 100%;`, css`background-color: ${rgba(colors.light, 0.1)};`]}
                />
            </div>
        </section>
    </SurfaceLayerProvider></BodyPartProvider>
}

function TrainingGoalSection({ trainingGoalOptions, selectedTrainingGoal, setSelectedTrainingGoal, indexData, pitch, showExpired, ...props }) {

    const { formatMessage } = useIntl()
    const selectedIndexResult = indexData?.find(ind => ind.indexCategory.id === selectedTrainingGoal)

    return <section css={css`
        width: 24em; 
        display: flex;
        flex-direction: column;
        @media screen and (max-width: 600px) {
            width: 20em;
        }
    `}>
        <h3 css={[css`margin-bottom: 0.875em;`, appearance.h3]}>
            {formatMessage({ id: "trainingGoal" })}
        </h3>
        <FancyDropdown
            css={css`margin-bottom: 0.25em; z-index: 2;`}
            value={trainingGoalOptions.find(opt => opt.value === selectedTrainingGoal)}
            options={trainingGoalOptions}
            onChange={(opt) => setSelectedTrainingGoal(opt?.value ?? null)}
            subtle={true}
            tint={colors.main2}
        />
        {selectedTrainingGoal !== null && //selectedIndexResult &&
            <TrainingGoalZones
                backgroundType={pitch?.pitchBackground || pitch?.club?.sport}
                zoneResults={selectedIndexResult?._isExpired_ && !showExpired ? [] : selectedIndexResult?.indexZoneResults}
                templateIRI={selectedIndexResult?.indexCategory?.pitchTemplate?.['@id']}
                orientation={pitch?.orientation}
                css={css`${selectedIndexResult?._isExpired_ ? style.isExpired(showExpired) : ''}`}
            />
        }
    </section>
}

function BootSection({ bootType, bootName, setShowBootModal, showExpired, ...props }) {

    if (!bootType) return null
    const { formatMessage } = useIntl()

    return <section
        css={[
            css`
            width: min-content;
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
            appearance.bootSection
        ]}
        onClick={() => setShowBootModal(true)}
        {...props}
    >

        <h3 css={[css`align-self: start; margin-bottom: 0.875em;`, appearance.h3,]}>
            {formatMessage({ id: "wear" })}
        </h3>

        <BootIndexIcon
            color={colors.light}
            bootBackendCode={bootType}
            bootNameLocalised={bootName}
            hoverOverlayText={false}
            style={{
                width: '6em',
                marginBottom: '1em',
                opacity: 0.75
            }}
        />

        <p css={[
            css`
                width: 100%;
                flex-grow: 1;
                display: grid; 
                place-items: center;
                text-align: center;
                padding: 0 0.3em;
            `,
            appearance.bootLabel,
        ]}>
            {bootName}
        </p>
    </section>
}

const appearance = {
    header: css`
        font-size: 1.3em;
        padding: 0.5em 1em; 
        border-radius: 5px;
        background: rgba(56,66,74, 0.8);
    `,
    h3: css`
        font-size: 1.75em; 
        font-weight: 200; 
        line-height: 1; 
        font-family: ${fonts.special}; 
        color: ${colors.subtle}; 
    `,
    bootSection: css`
        svg {
            transform-origin: center;
            color: ${colors.main2};
            &:hover {
                cursor: pointer;
                transform: scale(0.95);
                transition: transform, color, ${timings.snappy} ease-in-out;
            }
        }
    `,
    bootLabel: css`
        border-radius: 5px;
        background-color: ${rgba(colors.light, 0.1)};
    `,
    allExpired: css`
        color: white; 
        text-decoration: underline;
        cursor: pointer;
        &:hover {
            color: ${colors.main1};
        }
    `
}


const style = {
    card: (color) => css`
        text-decoration: none;
        display: block;
        border-radius: 0.3125em;
        overflow: hidden;
        padding: 1.25em;
        color: ${colors.white};
        position: relative;
        z-index: 0;
        transition: all ${timings.responsive} ease;
        box-shadow: 0 0.25em 0.75em 0 rgba(0,0,0,0.3);
        &::before {
            content: '';
            position: absolute;
            display: block;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: ${colors.stuff};
            z-index: -2;
            transition: all ${timings.responsive} ease;
            opacity: 0.66;
        }
        &::after {
            content: '';
            position: absolute;
            display: block;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background-image: linear-gradient(170deg, rgba(255,255,255,0) 50%, ${color});
            z-index: -1;
            transition: all ${timings.responsive} ease;
            opacity: 0.15;
        }
    `,
    title: css`
        display: inline-block;
        font-size: 1.25em;
        line-height: 1.2;
        font-family: ${fonts.special};
        font-weight: 700;
        text-transform: uppercase;
    `,
    aggregates: css`
        display: flex;
        justify-content: space-between;
        margin-top: 0.2em;
        align-items: center;
        flex-direction: row-reverse;
        background: rgba(146,153,170,0.1);
        border-radius: 5px;
        padding: 0.2em 1em;

    `,
    bodyWrapper: css`
        padding: 1em;
        display: flex;
        flex-wrap: wrap;
        gap: 2em;
        justify-content: center;

        @media screen and (max-width: 800px) {
            flex-direction: column;
            align-items: center;
            
            > * {
                max-width: 600px;
                margin-bottom: 1em;
                width: 100%;
            }
        }
    `,
    isExpired: (showExpired) => css`
        ${!showExpired && 'display: none;'}
        opacity: 0.5; 
        filter: grayscale(1);
        &:hover {
            filter: unset;
            opacity: 1;    
        }
    `,
    header: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        @media screen and (max-width: 800px) {
            flex-direction: column;
            gap: 1em;
            align-items: center;
        }
    `,
    headerLeftSection: css`
        display: flex;
        flex-direction: row;
        gap: 0 2em;
        @media screen and (max-width: 1600px) {
            flex-direction: column;
        }
    `,
    headerPitch: css`
        display: flex; 
        align-items: center; 
        gap: 1em; 
        margin-left: 0.7em;
    `,
    headerBestSuited: css`
        display: flex;
        align-items: center;
        font-family: ${fonts.special};
        text-transform: uppercase;
        
        > * {
            font-size: 1.2em;
        }
    `,
    bestSuitedResult: css`
        cursor: pointer;
        &:hover {
            color: ${colors.main1};
        }
        display: flex;
        align-items: center;
        gap: 0.5em;
    `
}